import { api } from './client';

// Slice 2: Auth-API. Backend-Endpunkte siehe functions/trips/[[path]].js:
//   POST /api/auth/register  {username, password, displayName?, tripPassword?, inviteToken?}
//   POST /api/auth/login     {username, password}
//   POST /api/auth/logout    {}
//   POST /api/invite         {} (eingeloggter User mit canInvite-Flag)

export interface RegisterInput {
  username: string;
  password: string;
  displayName?: string;
  tripPassword?: string;
  inviteToken?: string;
}

export interface LoginInput {
  username: string;
  password: string;
}

export interface AuthSuccess {
  ok: true;
  username: string;
  token: string;
  name: string;
  /** Nur bei /register: Anzahl umgehängter Legacy-Einträge. */
  migrated?: { votes: number; drinks: number; posts: number };
}

export interface InviteSuccess {
  ok: true;
  token: string;
  /** Nur bei Legacy-Einmal-Codes gesetzt; der dauerhafte Trip-Link hat kein Ablaufdatum. */
  expiresAt?: string;
}

const base = (slug: string) => `/trips/${encodeURIComponent(slug)}/api`;

export function login(slug: string, input: LoginInput): Promise<AuthSuccess> {
  return api.post<AuthSuccess>(`${base(slug)}/auth/login`, input);
}

export function register(slug: string, input: RegisterInput): Promise<AuthSuccess> {
  return api.post<AuthSuccess>(`${base(slug)}/auth/register`, input);
}

export function logout(slug: string): Promise<{ ok: true }> {
  return api.post<{ ok: true }>(`${base(slug)}/auth/logout`);
}

export function createInvite(slug: string): Promise<InviteSuccess> {
  return api.post<InviteSuccess>(`${base(slug)}/invite`);
}

// Backend-Validierungs-Regeln (1:1 aus functions/trips/[[path]].js).
// Werden auch im Client für sofortiges Inline-Feedback genutzt — der Server
// validiert defensiv weiter, das ist die Quelle der Wahrheit.
export const USERNAME_REGEX = /^[A-Za-z0-9_-]{3,32}$/;
export const PASSWORD_MIN = 6;
export const PASSWORD_MAX = 200;

export function validateUsername(value: string): string | null {
  if (!value) return 'Benutzername ist erforderlich.';
  if (!USERNAME_REGEX.test(value)) return '3–32 Zeichen, nur A–Z, 0–9, _ oder -.';
  return null;
}

export function validatePassword(value: string): string | null {
  if (value.length < PASSWORD_MIN) return `Mindestens ${PASSWORD_MIN} Zeichen.`;
  if (value.length > PASSWORD_MAX) return `Maximal ${PASSWORD_MAX} Zeichen.`;
  return null;
}

// Backend-Error-Codes → menschenlesbare deutsche Texte.
// Unbekannte Codes fallen auf den rohen Text zurück.
const ERROR_MESSAGES: Record<string, string> = {
  rate_limited: 'Zu viele Versuche. Bitte einen Moment warten.',
  invalid_username: 'Benutzername hat ein ungültiges Format.',
  invalid_password: 'Passwort hat ein ungültiges Format.',
  invalid_invite: 'Trip-Passwort ist falsch.',
  invalid_invite_token: 'Einladungs-Code ist ungültig oder abgelaufen.',
  username_taken: 'Dieser Benutzername ist bereits vergeben.',
  invalid_credentials: 'Benutzername oder Passwort falsch.',
  account_disabled: 'Dieser Account ist deaktiviert.',
  forbidden: 'Keine Berechtigung.',
  unauthorized: 'Bitte einloggen.',
  kv_missing: 'Server-Konfigurationsfehler.',
  invalid_json: 'Ungültige Anfrage.',
};

export function authErrorMessage(code: string | undefined): string {
  if (!code) return 'Unbekannter Fehler.';
  return ERROR_MESSAGES[code] ?? code;
}
